'use client';

/* eslint-disable @next/next/no-img-element */

import { useMemo, useState } from 'react';
import Link from 'next/link';
import {
  ArrowLeft,
  CalendarDays,
  Check,
  CheckCircle2,
  Clock3,
  Loader2,
  MapPin,
  MessageCircle,
  Scissors,
  ShieldCheck,
  Store,
  UserRound,
} from 'lucide-react';
import { requestAppointmentOtp, verifyAppointmentOtp, type AppointmentOtpClient } from '@/lib/booking/appointment-otp';
import { createProfessionalAppointment } from '@/lib/dashboard/create-appointment';
import type {
  ProfileProfessional,
  ProfileScheduleDay,
  ProfileService,
  PublicBusinessProfile,
} from '@/lib/dashboard/public-business-profile';
import { cn } from '@/lib/utils';

interface ProfileBookingFlowProps {
  profile: PublicBusinessProfile;
  professionalId?: string;
  serviceId?: string;
}

type BookingStep = 0 | 1 | 2 | 3;
type SubmitStatus = 'idle' | 'sending_otp' | 'otp_sent' | 'verifying_otp' | 'otp_verified' | 'confirming' | 'confirmed';

type DateOption = {
  date: string;
  disabled: boolean;
  label: string;
  shortLabel: string;
};

type SlotOption = {
  endAt: string;
  endLabel: string;
  id: string;
  label: string;
  startAt: string;
};

const STEPS = [
  { icon: Scissors, label: 'Servicio' },
  { icon: UserRound, label: 'Profesional' },
  { icon: CalendarDays, label: 'Fecha' },
  { icon: CheckCircle2, label: 'Finalizar' },
] as const;

function formatCurrency(value: number | null): string {
  if (value === null) return 'A convenir';
  return `$${new Intl.NumberFormat('es-CO').format(value)}`;
}

function formatDuration(value: number | null): string {
  return value ? `${value} min` : 'Duracion por confirmar';
}

function getInitials(name: string): string {
  const words = name.trim().split(/\s+/).slice(0, 2);
  return words.map((word) => word[0]?.toUpperCase()).join('') || 'OY';
}

function normalizeServiceDurationMinutes(rawDuration: number | null, fallbackMinutes = 30): number {
  const duration = Number(rawDuration);
  const fallback = Number(fallbackMinutes) || 30;
  const safeMinutes = Number.isFinite(duration) && duration > 0 ? duration : fallback;
  return Math.ceil(safeMinutes / 15) * 15;
}

function formatDateInTimeZone(date: Date, timeZone: string): string {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const parts = formatter.formatToParts(date);
  const year = parts.find((part) => part.type === 'year')?.value ?? '0000';
  const month = parts.find((part) => part.type === 'month')?.value ?? '01';
  const day = parts.find((part) => part.type === 'day')?.value ?? '01';
  return `${year}-${month}-${day}`;
}

function formatDateLabel(date: string, timeZone: string): string {
  const value = buildDateTimeInTimeZone(date, '12:00', timeZone);
  return new Intl.DateTimeFormat('es-CO', {
    day: 'numeric',
    month: 'long',
    timeZone,
    weekday: 'long',
  }).format(value);
}

function formatTimeInTimeZone(date: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('es-CO', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone,
  }).format(date);
}

function getWeekdayFromDate(yyyyMmDd: string): number {
  const [yearRaw, monthRaw, dayRaw] = yyyyMmDd.split('-');
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  const day = Number(dayRaw);
  if (!year || !month || !day) return 1;
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  return weekday === 0 ? 7 : weekday;
}

function normalizeTime(value: string): string {
  const [hourRaw, minuteRaw = '0'] = value.split(':');
  const hour = String(Math.max(0, Math.min(23, Number(hourRaw) || 0))).padStart(2, '0');
  const minute = String(Math.max(0, Math.min(59, Number(minuteRaw) || 0))).padStart(2, '0');
  return `${hour}:${minute}`;
}

function getTimeZoneOffsetMinutes(date: Date, timeZone: string): number {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    timeZoneName: 'shortOffset',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const offset = formatter.formatToParts(date).find((part) => part.type === 'timeZoneName')?.value;
  if (!offset || offset === 'GMT') return 0;
  const match = offset.match(/^GMT([+-])(\d{1,2})(?::?(\d{2}))?$/);
  if (!match) return 0;
  const sign = match[1] === '-' ? -1 : 1;
  return sign * (Number(match[2] ?? '0') * 60 + Number(match[3] ?? '0'));
}

function buildDateTimeInTimeZone(yyyyMmDd: string, hhMm: string, timeZone: string): Date {
  const [yearRaw, monthRaw, dayRaw] = yyyyMmDd.split('-');
  const [hourRaw, minuteRaw = '0'] = hhMm.split(':');
  const utcGuessMillis = Date.UTC(
    Number(yearRaw),
    Number(monthRaw) - 1,
    Number(dayRaw),
    Number(hourRaw),
    Number(minuteRaw),
    0,
    0
  );
  const utcGuessDate = new Date(utcGuessMillis);
  const offsetMinutes = getTimeZoneOffsetMinutes(utcGuessDate, timeZone);
  return new Date(utcGuessMillis - offsetMinutes * 60 * 1000);
}

function nextISODate(yyyyMmDd: string): string {
  const date = new Date(`${yyyyMmDd}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().slice(0, 10);
}

function scheduleForDate(schedules: ProfileScheduleDay[], date: string): ProfileScheduleDay | null {
  const weekday = getWeekdayFromDate(date);
  return schedules.find((schedule) => schedule.day === weekday) ?? null;
}

function buildDateOptions(profile: PublicBusinessProfile, professional: ProfileProfessional | null): DateOption[] {
  const timeZone = profile.timezone || 'America/Bogota';
  const horizonDays = Math.max(1, Math.min(10, profile.config.appointmentHorizonDays || 10));
  const now = new Date();

  return Array.from({ length: horizonDays }).map((_, index) => {
    const cursor = new Date(now);
    cursor.setDate(now.getDate() + index);
    const date = formatDateInTimeZone(cursor, timeZone);
    const schedule = professional ? scheduleForDate(professional.schedules, date) : null;
    const disabled = !schedule?.ranges.length;
    const shortDate = new Intl.DateTimeFormat('es-CO', {
      day: '2-digit',
      month: 'short',
      timeZone,
      weekday: 'short',
    }).format(buildDateTimeInTimeZone(date, '12:00', timeZone));

    return {
      date,
      disabled,
      label: index === 0 ? 'Hoy' : index === 1 ? 'Manana' : shortDate.split(',')[0] || shortDate,
      shortLabel: shortDate.replace('.', ''),
    };
  });
}

function buildSlots({
  date,
  profile,
  professional,
  service,
}: {
  date: string | null;
  profile: PublicBusinessProfile;
  professional: ProfileProfessional | null;
  service: ProfileService | null;
}): SlotOption[] {
  if (!date || !professional || !service) return [];

  const timeZone = profile.timezone || 'America/Bogota';
  const schedule = scheduleForDate(professional.schedules, date);
  if (!schedule?.ranges.length) return [];

  const serviceMinutes = normalizeServiceDurationMinutes(service.durationMinutes, profile.config.slotMinutes);
  const today = formatDateInTimeZone(new Date(), timeZone);
  const now = new Date();
  const slots: SlotOption[] = [];

  schedule.ranges.forEach((range) => {
    const start = buildDateTimeInTimeZone(date, normalizeTime(range.start), timeZone);
    let end = buildDateTimeInTimeZone(date, normalizeTime(range.end), timeZone);
    if (end <= start) {
      end = buildDateTimeInTimeZone(nextISODate(date), normalizeTime(range.end), timeZone);
    }

    let cursor = new Date(start);
    while (cursor < end) {
      const slotStart = new Date(cursor);
      const slotEnd = new Date(slotStart.getTime() + serviceMinutes * 60 * 1000);
      if (slotEnd > end) break;

      if (date !== today || slotStart > now) {
        slots.push({
          endAt: slotEnd.toISOString(),
          endLabel: formatTimeInTimeZone(slotEnd, timeZone),
          id: `${date}-${slotStart.toISOString()}`,
          label: formatTimeInTimeZone(slotStart, timeZone),
          startAt: slotStart.toISOString(),
        });
      }

      cursor = new Date(cursor.getTime() + 15 * 60 * 1000);
    }
  });

  return slots;
}

function BusinessImage({ profile }: { profile: PublicBusinessProfile }) {
  return (
    <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border-strong bg-bg-primary">
      {profile.imageUrl ? (
        <img src={profile.imageUrl} alt={profile.name} className="h-full w-full object-contain p-1.5" />
      ) : (
        <span className="font-heading text-sm font-semibold text-accent">{getInitials(profile.name)}</span>
      )}
    </div>
  );
}

function PersonImage({ professional }: { professional: ProfileProfessional }) {
  return (
    <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border-strong bg-bg-secondary">
      {professional.imageUrl ? (
        <img src={professional.imageUrl} alt={professional.name} className="h-full w-full object-cover" />
      ) : (
        <span className="font-heading text-sm font-semibold text-accent">{getInitials(professional.name)}</span>
      )}
    </div>
  );
}

function getStepForSelection(service: ProfileService | null, professional: ProfileProfessional | null): BookingStep {
  if (service && professional) return 2;
  if (service) return 1;
  return 0;
}

export function ProfileBookingFlow({
  profile,
  professionalId,
  serviceId,
}: ProfileBookingFlowProps) {
  const initialService = profile.services.find((service) => service.id === serviceId) ?? null;
  const initialProfessional = profile.professionals.find((professional) => professional.id === professionalId) ?? null;

  const [selectedServiceId, setSelectedServiceId] = useState<string | null>(initialService?.id ?? null);
  const [selectedProfessionalId, setSelectedProfessionalId] = useState<string | null>(initialProfessional?.id ?? null);
  const [currentStep, setCurrentStep] = useState<BookingStep>(() => getStepForSelection(initialService, initialProfessional));
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [clientName, setClientName] = useState('');
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [otpClient, setOtpClient] = useState<AppointmentOtpClient | null>(null);
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [feedback, setFeedback] = useState<string | null>(null);
  const [appointmentId, setAppointmentId] = useState<string | null>(null);

  const selectedService = useMemo(
    () => profile.services.find((service) => service.id === selectedServiceId) ?? null,
    [profile.services, selectedServiceId]
  );
  const selectedProfessional = useMemo(
    () => profile.professionals.find((professional) => professional.id === selectedProfessionalId) ?? null,
    [profile.professionals, selectedProfessionalId]
  );
  const selectableServices = useMemo(() => {
    if (!selectedProfessional || selectedProfessional.serviceIds.length === 0) return profile.services;
    return profile.services.filter((service) => selectedProfessional.serviceIds.includes(service.id));
  }, [profile.services, selectedProfessional]);
  const selectableProfessionals = useMemo(() => {
    if (!selectedService) return profile.professionals;
    return profile.professionals.filter(
      (professional) => professional.serviceIds.length === 0 || professional.serviceIds.includes(selectedService.id)
    );
  }, [profile.professionals, selectedService]);
  const dateOptions = useMemo(() => buildDateOptions(profile, selectedProfessional), [profile, selectedProfessional]);
  const slots = useMemo(
    () => buildSlots({ date: selectedDate, profile, professional: selectedProfessional, service: selectedService }),
    [profile, selectedDate, selectedProfessional, selectedService]
  );
  const selectedSlot = slots.find((slot) => slot.id === selectedSlotId) ?? null;

  const canGoToStep = (step: BookingStep) => {
    if (step === 0) return true;
    if (step === 1) return Boolean(selectedService);
    if (step === 2) return Boolean(selectedService && selectedProfessional);
    return Boolean(selectedService && selectedProfessional && selectedSlot);
  };

  const resetConfirmation = () => {
    setChallengeId(null);
    setOtp('');
    setOtpClient(null);
    setClientName('');
    setStatus('idle');
    setFeedback(null);
    setAppointmentId(null);
  };

  const selectService = (service: ProfileService) => {
    setSelectedServiceId(service.id);
    if (
      selectedProfessional &&
      selectedProfessional.serviceIds.length > 0 &&
      !selectedProfessional.serviceIds.includes(service.id)
    ) {
      setSelectedProfessionalId(null);
      setSelectedDate(null);
      setSelectedSlotId(null);
    }
    resetConfirmation();
    setCurrentStep(1);
  };

  const selectProfessional = (professional: ProfileProfessional) => {
    setSelectedProfessionalId(professional.id);
    setSelectedDate(null);
    setSelectedSlotId(null);
    resetConfirmation();
    setCurrentStep(2);
  };

  const selectDate = (date: string) => {
    setSelectedDate(date);
    setSelectedSlotId(null);
    resetConfirmation();
  };

  const selectSlot = (slot: SlotOption) => {
    setSelectedSlotId(slot.id);
    resetConfirmation();
    setCurrentStep(3);
  };

  const confirmAppointment = async (resolvedClientName?: string) => {
    if (!selectedService || !selectedProfessional || !selectedSlot || (profile.supportsWhatsapp && !challengeId)) {
      setFeedback('Completa los datos de la cita antes de confirmar.');
      return;
    }

    setStatus('confirming');
    setFeedback(null);

    try {
      const result = await createProfessionalAppointment({
        appointmentOtpChallengeId: challengeId ?? undefined,
        businessId: profile.id,
        clientName: resolvedClientName || clientName,
        clientPhone: phone,
        professionalId: selectedProfessional.id,
        publicBooking: true,
        reservadoVia: 'PAGINA',
        serviceTypeId: selectedService.id,
        startAt: selectedSlot.startAt,
      });

      setAppointmentId(result.appointmentId);
      setStatus('confirmed');
      setFeedback(result.message);
    } catch (error) {
      setStatus('otp_verified');
      setFeedback(error instanceof Error ? error.message : 'No fue posible confirmar la cita.');
    }
  };

  const handleRequestOtp = async () => {
    if (!selectedService || !selectedProfessional || !selectedSlot) {
      setFeedback('Selecciona servicio, profesional y hora antes de confirmar.');
      return;
    }

    setStatus('sending_otp');
    setFeedback(null);
    setChallengeId(null);
    setOtpClient(null);

    try {
      const result = await requestAppointmentOtp({
        businessId: profile.id,
        phone,
        professionalId: selectedProfessional.id,
        serviceId: selectedService.id,
        startAt: selectedSlot.startAt,
      });
      setChallengeId(result.challengeId);
      setStatus('otp_sent');
      setFeedback(`Enviamos un codigo por WhatsApp. Expira en ${Math.round(result.expiresInSeconds / 60)} min.`);
    } catch (error) {
      setStatus('idle');
      setFeedback(error instanceof Error ? error.message : 'No fue posible enviar el codigo.');
    }
  };

  const handleVerifyOtp = async () => {
    if (!challengeId) {
      setFeedback('Solicita primero el codigo por WhatsApp.');
      return;
    }

    setStatus('verifying_otp');
    setFeedback(null);

    try {
      const client = await verifyAppointmentOtp({ challengeId, otp, phone });
      setOtpClient(client);
      setStatus('otp_verified');

      if (!client.requiresName) {
        await confirmAppointment(client.name ?? undefined);
        return;
      }

      setFeedback('Numero confirmado. Ingresa el nombre para dejar la cita registrada.');
    } catch (error) {
      setStatus('otp_sent');
      setFeedback(error instanceof Error ? error.message : 'Codigo invalido o expirado.');
    }
  };

  const selectedDateLabel = selectedDate ? formatDateLabel(selectedDate, profile.timezone) : null;

  return (
    <main className="fixed inset-0 z-[60] w-full max-w-full overflow-x-hidden overflow-y-auto bg-bg-primary text-text-primary">
      <header className="sticky top-0 z-30 border-b border-border bg-bg-secondary/95 backdrop-blur-md">
        <div className="mx-auto flex w-full min-w-0 max-w-7xl flex-col gap-4 overflow-hidden px-4 py-4 sm:px-6 lg:flex-row lg:items-center">
          <div className="flex min-w-0 items-center gap-3 lg:contents">
            <Link
              href={`/perfiles/${profile.slug}/`}
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-button border border-border-strong text-text-secondary transition-colors hover:border-accent hover:text-accent lg:hidden"
              aria-label="Volver al perfil"
            >
              <ArrowLeft size={18} />
            </Link>
            <div className="flex min-w-0 items-center gap-3">
              <BusinessImage profile={profile} />
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase text-accent">Reserva</p>
                <h1 className="truncate font-heading text-lg font-semibold text-text-primary sm:text-xl">{profile.name}</h1>
              </div>
            </div>
          </div>

          <nav className="grid w-full min-w-0 grid-cols-4 gap-1.5 lg:ml-auto lg:w-auto lg:min-w-[560px] lg:gap-2" aria-label="Pasos de reserva">
            {STEPS.map((step, index) => {
              const Icon = step.icon;
              const isActive = currentStep === index;
              const isDone = currentStep > index || (index === 3 && status === 'confirmed');
              const enabled = canGoToStep(index as BookingStep);

              return (
                <button
                  key={step.label}
                  type="button"
                  onClick={() => enabled && setCurrentStep(index as BookingStep)}
                  disabled={!enabled}
                  className={cn(
                    'flex min-h-11 min-w-0 items-center justify-center gap-1 rounded-lg border px-1 text-xs font-heading font-semibold transition-colors sm:gap-2 sm:px-2 sm:text-sm',
                    isActive
                      ? 'border-accent bg-accent text-bg-primary'
                      : isDone
                        ? 'border-success/40 bg-success/10 text-success'
                        : 'border-border-strong bg-bg-primary text-text-secondary',
                    !enabled && 'cursor-not-allowed opacity-50'
                  )}
                >
                  {isDone ? <Check size={15} className="shrink-0" /> : <Icon size={15} className="shrink-0" />}
                  <span className="min-w-0 truncate">{step.label}</span>
                </button>
              );
            })}
          </nav>
          <Link
            href={`/perfiles/${profile.slug}/`}
            className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-button border border-border-strong text-text-secondary transition-colors hover:border-accent hover:text-accent lg:inline-flex"
            aria-label="Volver al perfil"
          >
            <ArrowLeft size={18} />
          </Link>
        </div>
      </header>

      <div className="mx-auto grid w-full min-w-0 max-w-7xl gap-6 px-4 py-6 sm:px-6 lg:grid-cols-[minmax(0,1fr)_380px]">
        <section className="min-w-0">
          {currentStep === 0 ? (
            <div className="space-y-6">
              <div>
                <h2 className="font-heading text-2xl font-semibold">Elige el servicio</h2>
                <p className="mt-1 text-sm text-text-secondary">Selecciona lo que quieres reservar.</p>
              </div>

              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {selectableServices.map((service) => {
                  const selected = selectedService?.id === service.id;
                  return (
                    <button
                      key={service.id}
                      type="button"
                      onClick={() => selectService(service)}
                      className={cn(
                        'group grid w-full min-w-0 grid-cols-[6.5rem_minmax(0,1fr)] overflow-hidden rounded-lg border bg-bg-secondary text-left transition-colors hover:border-accent/70 sm:block',
                        selected ? 'border-accent' : 'border-border'
                      )}
                    >
                      <div className="min-h-[8rem] bg-bg-primary sm:aspect-[4/3] sm:min-h-0">
                        {service.imageUrl ? (
                          <img src={service.imageUrl} alt={service.name} className="h-full w-full object-cover" />
                        ) : (
                          <div className="flex h-full items-center justify-center text-accent">
                            <Scissors size={30} />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 space-y-2 px-3 py-3 sm:space-y-3 sm:px-4 sm:py-4">
                        <div className="flex items-start justify-between gap-3">
                          <h3 className="line-clamp-2 min-w-0 break-words font-heading text-base font-semibold sm:text-lg">{service.name}</h3>
                          {selected ? <CheckCircle2 size={18} className="shrink-0 text-accent" /> : null}
                        </div>
                        {service.description ? <p className="line-clamp-2 text-sm text-text-secondary">{service.description}</p> : null}
                        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-sm">
                          <span className="font-semibold text-accent">{formatCurrency(service.priceCop)}</span>
                          <span className="text-text-secondary">{formatDuration(service.durationMinutes)}</span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          {currentStep === 1 ? (
            <div className="space-y-6">
              <div>
                <h2 className="font-heading text-2xl font-semibold">Elige el profesional</h2>
                <p className="mt-1 text-sm text-text-secondary">Solo aparecen profesionales que pueden atender el servicio seleccionado.</p>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                {selectableProfessionals.map((professional) => {
                  const selected = selectedProfessional?.id === professional.id;
                  return (
                    <button
                      key={professional.id}
                      type="button"
                      onClick={() => selectProfessional(professional)}
                      className={cn(
                        'flex items-center gap-4 rounded-lg border bg-bg-secondary px-4 py-4 text-left transition-colors hover:border-accent/70',
                        selected ? 'border-accent' : 'border-border'
                      )}
                    >
                      <PersonImage professional={professional} />
                      <div className="min-w-0 flex-1">
                        <h3 className="truncate font-heading text-lg font-semibold">{professional.name}</h3>
                        <p className="mt-1 text-sm text-text-secondary">Disponible para reservas</p>
                      </div>
                      <span
                        className={cn(
                          'inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-button border',
                          selected ? 'border-accent bg-accent text-bg-primary' : 'border-border-strong text-text-secondary'
                        )}
                      >
                        <Check size={18} />
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          {currentStep === 2 ? (
            <div className="space-y-7">
              <div>
                <h2 className="font-heading text-2xl font-semibold">Elige fecha y hora</h2>
                <p className="mt-1 text-sm text-text-secondary">Puedes reservar dentro de la ventana de los proximos 10 dias.</p>
              </div>

              <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin">
                {dateOptions.map((option) => {
                  const selected = selectedDate === option.date;
                  return (
                    <button
                      key={option.date}
                      type="button"
                      onClick={() => !option.disabled && selectDate(option.date)}
                      disabled={option.disabled}
                      className={cn(
                        'flex min-h-20 min-w-[112px] flex-col items-center justify-center rounded-lg border px-3 text-center transition-colors',
                        selected
                          ? 'border-accent bg-accent text-bg-primary'
                          : option.disabled
                            ? 'cursor-not-allowed border-border bg-bg-secondary text-text-secondary/45'
                            : 'border-border-strong bg-bg-secondary text-text-primary hover:border-accent/70'
                      )}
                    >
                      <span className="font-heading text-sm font-semibold">{option.label}</span>
                      <span className="mt-1 text-xs opacity-80">{option.shortLabel}</span>
                    </button>
                  );
                })}
              </div>

              <div className="rounded-lg border border-border bg-bg-secondary px-4 py-5">
                <div className="mb-4 flex items-center justify-between gap-3">
                  <div>
                    <h3 className="font-heading text-lg font-semibold">Horas disponibles</h3>
                    <p className="mt-1 text-sm text-text-secondary">{selectedDateLabel ?? 'Selecciona primero un dia.'}</p>
                  </div>
                  <Clock3 size={20} className="text-accent" />
                </div>

                {selectedDate ? (
                  slots.length > 0 ? (
                    <div className="grid grid-cols-3 gap-2 lg:grid-cols-4">
                      {slots.map((slot) => {
                        const selected = selectedSlot?.id === slot.id;
                        return (
                          <button
                            key={slot.id}
                            type="button"
                            onClick={() => selectSlot(slot)}
                            className={cn(
                              'min-h-11 min-w-0 rounded-lg border px-1 py-2 font-heading text-sm font-semibold transition-colors sm:px-3',
                              selected
                                ? 'border-accent bg-accent text-bg-primary'
                                : 'border-border-strong bg-bg-primary text-text-primary hover:border-accent/70'
                            )}
                          >
                            {slot.label}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="rounded-lg border border-border-strong bg-bg-primary px-4 py-4 text-sm text-text-secondary">
                      No hay horas disponibles para este dia.
                    </p>
                  )
                ) : (
                  <p className="rounded-lg border border-border-strong bg-bg-primary px-4 py-4 text-sm text-text-secondary">
                    Elige uno de los dias habilitados de la fila superior.
                  </p>
                )}
              </div>
            </div>
          ) : null}

          {currentStep === 3 ? (
            <div className="space-y-6">
              <div>
                <h2 className="font-heading text-2xl font-semibold">Confirma la cita</h2>
                <p className="mt-1 text-sm text-text-secondary">
                  {profile.supportsWhatsapp
                    ? 'Validaremos el numero por WhatsApp antes de dejar la reserva confirmada.'
                    : 'Completa tus datos para dejar la reserva confirmada.'}
                </p>
              </div>

              {status === 'confirmed' ? (
                <div className="rounded-lg border border-success/40 bg-success/10 px-5 py-5 text-success">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 size={24} />
                    <div>
                      <p className="font-heading text-lg font-semibold">Cita confirmada</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border border-border bg-bg-secondary px-5 py-5">
                  <div className="grid gap-4 md:grid-cols-[1fr_auto] md:items-end">
                    <label className="block">
                      <span className="text-sm font-semibold text-text-primary">Teléfono de quien agenda</span>
                      <input
                        type="tel"
                        value={phone}
                        onChange={(event) => {
                          setPhone(event.target.value);
                          resetConfirmation();
                        }}
                        placeholder="+573001234567"
                        className="mt-2 min-h-11 w-full rounded-lg border border-border-strong bg-bg-primary px-3 text-text-primary placeholder:text-text-secondary/60"
                      />
                    </label>
                    {profile.supportsWhatsapp ? <button
                      type="button"
                      onClick={handleRequestOtp}
                      disabled={status === 'sending_otp' || !phone.trim()}
                      className="inline-flex min-h-11 items-center justify-center gap-2 rounded-button bg-accent px-5 py-3 font-heading font-semibold text-bg-primary transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {status === 'sending_otp' ? <Loader2 size={18} className="animate-spin" /> : <MessageCircle size={18} />}
                      Enviar codigo
                    </button> : null}
                  </div>

                  {!profile.supportsWhatsapp ? (
                    <div className="mt-5 grid gap-4 md:grid-cols-[1fr_auto] md:items-end">
                      <label className="block">
                        <span className="text-sm font-semibold text-text-primary">Nombre completo</span>
                        <input
                          value={clientName}
                          onChange={(event) => setClientName(event.target.value)}
                          placeholder="Nombre de la persona"
                          className="mt-2 min-h-11 w-full rounded-lg border border-border-strong bg-bg-primary px-3 text-text-primary placeholder:text-text-secondary/60"
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => confirmAppointment(clientName)}
                        disabled={status === 'confirming' || !phone.trim() || clientName.trim().split(/\s+/).length < 2}
                        className="inline-flex min-h-11 items-center justify-center gap-2 rounded-button bg-accent px-5 py-3 font-heading font-semibold text-bg-primary transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {status === 'confirming' ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle2 size={18} />}
                        Confirmar cita
                      </button>
                    </div>
                  ) : null}

                  {challengeId ? (
                    <div className="mt-5 grid gap-4 md:grid-cols-[1fr_auto] md:items-end">
                      <label className="block">
                        <span className="text-sm font-semibold text-text-primary">Codigo OTP</span>
                        <input
                          inputMode="numeric"
                          value={otp}
                          onChange={(event) => setOtp(event.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="123456"
                          className="mt-2 min-h-11 w-full rounded-lg border border-border-strong bg-bg-primary px-3 text-text-primary placeholder:text-text-secondary/60"
                        />
                      </label>
                      <button
                        type="button"
                        onClick={handleVerifyOtp}
                        disabled={status === 'verifying_otp' || otp.length < 6}
                        className="inline-flex min-h-11 items-center justify-center gap-2 rounded-button border border-accent px-5 py-3 font-heading font-semibold text-accent transition-colors hover:bg-accent hover:text-bg-primary disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {status === 'verifying_otp' ? <Loader2 size={18} className="animate-spin" /> : <ShieldCheck size={18} />}
                        Verificar
                      </button>
                    </div>
                  ) : null}

                  {otpClient?.requiresName ? (
                    <div className="mt-5 grid gap-4 md:grid-cols-[1fr_auto] md:items-end">
                      <label className="block">
                        <span className="text-sm font-semibold text-text-primary">Nombre completo</span>
                        <input
                          value={clientName}
                          onChange={(event) => setClientName(event.target.value)}
                          placeholder="Nombre de la persona"
                          className="mt-2 min-h-11 w-full rounded-lg border border-border-strong bg-bg-primary px-3 text-text-primary placeholder:text-text-secondary/60"
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => confirmAppointment(clientName)}
                        disabled={status === 'confirming' || clientName.trim().split(/\s+/).length < 2}
                        className="inline-flex min-h-11 items-center justify-center gap-2 rounded-button bg-accent px-5 py-3 font-heading font-semibold text-bg-primary transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {status === 'confirming' ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle2 size={18} />}
                        Confirmar cita
                      </button>
                    </div>
                  ) : null}

                  {otpClient && !otpClient.requiresName ? (
                    <div className="mt-5 flex justify-end">
                      <button
                        type="button"
                        onClick={() => confirmAppointment(otpClient.name ?? undefined)}
                        disabled={status === 'confirming'}
                        className="inline-flex min-h-11 items-center justify-center gap-2 rounded-button bg-accent px-5 py-3 font-heading font-semibold text-bg-primary transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {status === 'confirming' ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle2 size={18} />}
                        Confirmar cita
                      </button>
                    </div>
                  ) : null}
                </div>
              )}

              {feedback ? (
                <p
                  className={cn(
                    'rounded-lg border px-4 py-3 text-sm',
                    status === 'confirmed'
                      ? 'border-success/40 bg-success/10 text-success'
                      : 'border-border-strong bg-bg-secondary text-text-secondary'
                  )}
                >
                  {feedback}
                </p>
              ) : null}
            </div>
          ) : null}
        </section>

        <aside className="lg:sticky lg:top-28 lg:self-start">
          <div className="rounded-lg border border-border bg-bg-secondary px-5 py-5">
            <div className="flex items-start gap-3 border-b border-border pb-5">
              <BusinessImage profile={profile} />
              <div className="min-w-0">
                <div className="inline-flex items-center gap-1.5 rounded-button border border-accent/30 bg-accent/10 px-2 py-1 text-xs font-semibold text-accent">
                  <Store size={13} />
                  Negocio
                </div>
                <h2 className="mt-2 truncate font-heading text-lg font-semibold">{profile.name}</h2>
                {profile.address ? (
                  <p className="mt-1 flex items-start gap-2 text-sm leading-5 text-text-secondary">
                    <MapPin size={15} className="mt-0.5 shrink-0 text-accent" />
                    <span>{profile.address}</span>
                  </p>
                ) : null}
              </div>
            </div>

            <div className="space-y-4 py-5">
              <div className="rounded-lg border border-border-strong bg-bg-primary px-4 py-4">
                <p className="flex items-center gap-2 text-xs font-semibold uppercase text-text-secondary">
                  <Scissors size={14} className="text-accent" />
                  Servicio
                </p>
                <p className="mt-2 font-heading text-base font-semibold">{selectedService?.name ?? 'Por seleccionar'}</p>
                {selectedService ? (
                  <p className="mt-1 text-sm text-text-secondary">
                    {formatCurrency(selectedService.priceCop)} · {formatDuration(selectedService.durationMinutes)}
                  </p>
                ) : null}
              </div>

              <div className="rounded-lg border border-border-strong bg-bg-primary px-4 py-4">
                <p className="flex items-center gap-2 text-xs font-semibold uppercase text-text-secondary">
                  <UserRound size={14} className="text-accent" />
                  Profesional
                </p>
                <p className="mt-2 font-heading text-base font-semibold">{selectedProfessional?.name ?? 'Por seleccionar'}</p>
              </div>

              <div className="rounded-lg border border-border-strong bg-bg-primary px-4 py-4">
                <p className="flex items-center gap-2 text-xs font-semibold uppercase text-text-secondary">
                  <Clock3 size={14} className="text-accent" />
                  Fecha y hora
                </p>
                <p className="mt-2 font-heading text-base font-semibold">
                  {selectedDateLabel && selectedSlot ? `${selectedDateLabel}, ${selectedSlot.label}` : 'Por seleccionar'}
                </p>
                {selectedSlot ? <p className="mt-1 text-sm text-text-secondary">Termina aprox. {selectedSlot.endLabel}</p> : null}
              </div>

              <div className="rounded-lg border border-border-strong bg-bg-primary px-4 py-4">
                <p className="flex items-center gap-2 text-xs font-semibold uppercase text-text-secondary">
                  <MessageCircle size={14} className="text-accent" />
                  Contacto
                </p>
                <p className="mt-2 font-heading text-base font-semibold">{phone.trim() || 'Teléfono pendiente'}</p>
                <p className="mt-1 text-sm text-text-secondary">
                  {otpClient?.requiresName ? clientName || 'Nombre pendiente' : otpClient?.name || 'Se confirma al final'}
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setCurrentStep(3)}
              disabled={!canGoToStep(3)}
              className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-button bg-accent px-5 py-3 font-heading font-semibold text-bg-primary transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-60"
            >
              <CheckCircle2 size={18} />
              Ir a finalizar
            </button>
          </div>
        </aside>
      </div>
    </main>
  );
}
